/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import useTacticKanban from '../CepForm/Steps/TacticsStep/TacticKanban/TacticKanban.hook';
import useApprovedDocuments from './hooks/approvedDocuments.hook';
import useIVADocuments from './hooks/IVADocuments.hook';
import usePreviousTactics from './hooks/PreviousTactics.hook';
import useTacticDropMaterials from './hooks/TacticDropMaterials.hook';
import useTacticGroups from './hooks/TacticGroups.hook';
import useTacticKeyMessages from './hooks/TacticKeyMessages.hook';
import useTacticFormConfig from './TacticForm.config';
import { TacticFormConfigType } from './TacticForm.types';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import { InitiativeCountry } from 'basics/enums/country.enums';
import { CepStatus, Channel, TacticMode, TacticStatus, TacticSuffix } from 'basics/enums/maya.enums';
import { useBulkUpdateTacticMutation } from 'basics/graphql/mutations/bulkUpdateTactic';
import { useCreateTacticMutation } from 'basics/graphql/mutations/createTactic';
import { useCreateTacticGroupMutation } from 'basics/graphql/mutations/createTacticGroup';
import { useUpdateApprovedTacticMutation } from 'basics/graphql/mutations/updateApprovedTactic';
import { useUpdateTacticMutation } from 'basics/graphql/mutations/updateTactic';
import useGetCepByIdLazyQuery from 'basics/graphql/queries/getCepByIdLazy';
import useGetNextTacticsQuery from 'basics/graphql/queries/getNextTactics';
import { tacticGroupToBE, tacticToBE, tacticToBulkUpdateInput, tacticToTacticInput } from 'basics/transformers/Tactic.transformer';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import asyncMap from 'basics/utils/asyncMap';
import { getMaxNumberFromStringList } from 'basics/utils/getNumberFromString';
import { succesToast, warningToast } from 'basics/utils/toast';
import {
  defaultTacticVersion,
  minimumAllowedNumberOfTacticsInGroup,
  RequestFormUrlMapping,
  TherapeuticAreaRequestFormMapping,
} from 'components/TacticForm/TacticForm.constants';
import useTacticValidation from 'components/TacticValidationDialog/TacticValidation.hook';
import { CustomerEngagementPlanning, Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';
import { YodaFieldValue } from 'yoda-ui/yodaForm/yodaForm.types';

const useTacticCreate = (isTemplate: boolean) => {
  const flags = useFlags();
  const { t } = useTranslation();

  const {
    getValues,
    providerFields,
    resetField,
    resetForm,
    setDefaultValue,
    setError,
    setFieldShowError,
    setValue,
    useWatchField,
    useWatchForm,
  } = useYodaCreateForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;
  const [selectedTacticGroup, setSelectedTacticGroup] = useState<Tactic | null>(tacticStateValue?.tactic?.parentTactic || null);
  const [tacticTitlesInSelectedGroup, setTacticTitlesInSelectedGroup] = useState<string[]>([]);
  const executingTeam = useWatchField('executingTeam')?.value;
  const tacticGroupFieldValue = useWatchField('tacticGroup')?.value;
  const [openGroupForm, setOpenGroupForm] = useState(false);
  const [openTacticValidationDialog, setOpenTacticValidationDialog] = useState(false);
  const [tacticGroupLoading, setTacticGroupLoading] = useState(false);
  const channelKey = useWatchField('channel')?.value;
  const approvedDocumentId = useWatchField('approvedDocumentId')?.value;
  const IVADocumentId = useWatchField('IVADocumentId')?.value;
  const dropMaterialId = useWatchField('dropMaterial')?.value;
  const keyMessageId = useWatchField('keyMessage')?.value;

  const {
    dropMaterialsOptions,
    dropMaterialsLoading,
  } = useTacticDropMaterials(executingTeam, channelKey, setValue, dropMaterialId);

  const {
    keyMessagesOptions,
    keyMessagesLoading,
  } = useTacticKeyMessages(executingTeam, selectedChannel);

  const prevExecutingTeam = useRef(executingTeam);

  useEffect(() => {
    if (executingTeam !== prevExecutingTeam.current) {
      prevExecutingTeam.current = executingTeam;
      if (keyMessagesOptions.length > 0) {
        setValue('keyMessage', '');
      }
    }
  }, [executingTeam, keyMessagesOptions.length, setValue]);

  useEffect(() => {
    const formValues = getValues();
    const currentTactic = cepState.tactics.find((tactic) => tactic?._id === tacticStateValue.tactic?._id);

    if (!currentTactic?.parentTactic && formValues.title && formValues.tacticGroup) {
      setValue('tacticGroup', null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepState.tactics]);

  const { tacticGroupsOptions, showTacticGroup, tacticGroupDisabled } = useTacticGroups(channelKey, tacticGroupLoading);
  const { previousTacticOptions } = usePreviousTactics(getValues(), tacticGroupFieldValue, showTacticGroup);

  const {
    allIVADocumentsOptions,
    allVeevaVaultIVADocumentsLoading,
    showNotCertifiedIVADocuments,
    handleIVADocumentsFilterChanging,
  } = useIVADocuments(executingTeam, channelKey, IVADocumentId, setValue);

  const {
    allApprovedDocumentIdOptions,
    allVeevaVaultDocumentsLoading,
    showNotCertifiedDocuments,
    handleVAEFilterChanging,
  } = useApprovedDocuments(executingTeam, channelKey, approvedDocumentId, setValue);

  const isSelectedValueAvailableInOptions = (
    selectedValue: string | number | null | undefined,
    options: { id: string | number }[],
  ) => {
    if (!selectedValue) return true;

    return options.some((option) => option.id.toString() === selectedValue.toString());
  };

  useEffect(() => {
    if (
      !keyMessagesLoading
        && keyMessageId
        && keyMessagesOptions.length > 0
        && !isSelectedValueAvailableInOptions(keyMessageId, keyMessagesOptions)
    ) {
      setValue('keyMessage', '');
    }
  }, [keyMessageId, keyMessagesLoading, keyMessagesOptions, setValue]);

  useEffect(() => {
    const tacticDropMaterialId = tacticStateValue.tactic?.dropMaterial?.id;

    if (
      !dropMaterialsLoading
        && tacticDropMaterialId
        && !dropMaterialId
        && dropMaterialsOptions.length > 0
        && isSelectedValueAvailableInOptions(tacticDropMaterialId, dropMaterialsOptions)
    ) {
      setValue('dropMaterial', tacticDropMaterialId);
    }
  }, [
    dropMaterialId,
    dropMaterialsLoading,
    dropMaterialsOptions,
    setValue,
    tacticStateValue.tactic?.dropMaterial?.id,
  ]);

  useEffect(() => {
    if (
      !dropMaterialsLoading
        && dropMaterialId
        && dropMaterialsOptions.length > 0
        && !isSelectedValueAvailableInOptions(dropMaterialId, dropMaterialsOptions)
    ) {
      setValue('dropMaterial', '');
    }
  }, [dropMaterialId, dropMaterialsLoading, dropMaterialsOptions, setValue]);

  useEffect(() => {
    const tacticIVADocumentId = tacticStateValue.tactic?.IVADocumentId;

    if (
      !allVeevaVaultIVADocumentsLoading
        && tacticIVADocumentId
        && !IVADocumentId
        && allIVADocumentsOptions.length > 0
        && isSelectedValueAvailableInOptions(tacticIVADocumentId, allIVADocumentsOptions)
    ) {
      setValue('IVADocumentId', tacticIVADocumentId);
    }
  }, [
    IVADocumentId,
    allVeevaVaultIVADocumentsLoading,
    allIVADocumentsOptions,
    setValue,
    tacticStateValue.tactic?.IVADocumentId,
  ]);

  useEffect(() => {
    if (
      !allVeevaVaultIVADocumentsLoading
        && IVADocumentId
        && allIVADocumentsOptions.length > 0
        && !isSelectedValueAvailableInOptions(IVADocumentId, allIVADocumentsOptions)
    ) {
      setValue('IVADocumentId', '');
    }
  }, [IVADocumentId, allVeevaVaultIVADocumentsLoading, allIVADocumentsOptions, setValue]);

  useEffect(() => {
    const formValues = getValues();
    const isSamePrevAndParentTactic = tacticGroupFieldValue && tacticGroupFieldValue === formValues.previousTactic;
    if (isSamePrevAndParentTactic || !previousTacticOptions?.length) {
      setValue('previousTactic', null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousTacticOptions]);

  const [createTactic] = useCreateTacticMutation();
  const [updateTactic] = useUpdateTacticMutation();
  const [bulkUpdateTactic] = useBulkUpdateTacticMutation();
  const [createTacticGroup] = useCreateTacticGroupMutation();
  const [updateApprovedTactic] = useUpdateApprovedTacticMutation();
  const { queryGetCepById } = useGetCepByIdLazyQuery();
  const { getNumberOfTacticsInGroup } = useTacticValidation();
  const sendToVeeva = !isTemplate;

  const updateGroupsInTacticsState = (relatedTactics: Tactic[], group: Tactic) => {
    relatedTactics.forEach((tactic) => {
      cepState?.tactics?.forEach((stateTactic, index) => {
        if (stateTactic._id === tactic._id && tactic) {
          cepState.tactics[index].parentTactic = group;
        }
      });
    });
  };

  const updateTacticRelationsOnApprovedCEP = async (
    relatedTacticsId: string[],
    tacticGroupData: Tactic | null,
    newPreviousTactic: Tactic | null,
    cepId: string,
  ) => {
    const tactics = cepStateValue?.tactics.filter((tactic) => relatedTacticsId.includes(tactic._id));
    let isError = false;

    await asyncMap(tactics, async (tactic) => {
      const formattedTactic = tacticToTacticInput({
        ...tactic,
        parentTactic: tacticGroupData,
        previousTactic: newPreviousTactic,
      }, cepId);

      if (formattedTactic.version) {
        formattedTactic.version += 1;
      }

      const { error: errorStatus } = await updateApprovedTactic(tactic._id, formattedTactic, cepId, false, sendToVeeva);

      if (errorStatus) {
        isError = true;
        warningToast(errorStatus || t('errors_standard'));
      }
    });

    if (!isError && tacticGroupData) {
      updateGroupsInTacticsState(tactics, tacticGroupData);
      succesToast(t('maya_tactic_form_group_related_tactic_updated'));
    }

    setTacticGroupLoading(false);
  };

  const updateTacticRelationsOnNotApprovedCEP = async (
    relatedTacticsId: string[],
    tacticGroupData: Tactic | null,
    newPreviousTactic: Tactic | null,
    cepId: string,
  ) => {
    const tactics = cepStateValue?.tactics.filter((tactic) => relatedTacticsId.includes(tactic._id));
    const tacticsToBulkUpdate = tactics.map((tactic) => {
      return tacticToBulkUpdateInput({
        ...tactic,
        parentTactic: tacticGroupData,
        previousTactic: newPreviousTactic,
      }, cepId);
    });

    const {
      data: updateTactics,
      error: bulkError,
    } = await bulkUpdateTactic(cepId, tacticsToBulkUpdate, sendToVeeva, tacticGroupData?._id);

    if (!bulkError && updateTactics && tacticGroupData) {
      updateGroupsInTacticsState(tactics, tacticGroupData);
      succesToast(t('maya_tactic_form_group_related_tactic_updated'));
      setTacticGroupLoading(false);
      return;
    } if (bulkError) {
      warningToast(bulkError || t('errors_standard'));
    }
  };

  const handleTacticGroupCreation = async (relatedTacticsId: string[]) => {
    const syncToVeeva = cepStateValue.cep?.status === CepStatus.APPROVED;

    setTacticGroupLoading(true);

    const groupTitles = cepStateValue.tacticGroups.map((group) => group.title);
    const groupNumber = getMaxNumberFromStringList(groupTitles) || 0;
    const title = `${t('maya_tactic_form_group_name')} ${groupNumber + 1}`;
    const formattedTacticGroup = tacticGroupToBE(title, tacticStateValue.cepId, defaultTacticVersion, TacticStatus.APPROVED);

    const { data: tacticGroupData, error } = await createTacticGroup(
      formattedTacticGroup,
      tacticStateValue.cepId,
      relatedTacticsId,
      syncToVeeva,
    );

    const cepId = cepStateValue.cep?._id;

    if (!error && tacticGroupData) {
      cepState.tacticGroups.push(tacticGroupData);
      setSelectedTacticGroup(tacticGroupData);
      succesToast(`${tacticGroupData?.title}${t('maya_tactic_form_group_creation')}`);
    } else if (error) {
      warningToast(error || t('errors_standard'));
      setTacticGroupLoading(false);
      return;
    }

    const tacticGroups = cepStateValue?.tacticGroups.filter((group) => {
      const tacticsInGroup = cepStateValue.tactics?.filter((tacticItem) => tacticItem?.parentTactic?._id === group?._id);
      return tacticsInGroup.length >= minimumAllowedNumberOfTacticsInGroup;
    });

    const previousTacticOptionsForGroup = previousTacticOptions?.filter((prevOption) => {
      return tacticGroups.find((group) => group._id === prevOption.id);
    });

    if (previousTacticOptionsForGroup?.length) {
      const lastPrevTactic = previousTacticOptionsForGroup.pop();
      setValue('previousTactic', lastPrevTactic?.id);
    } else {
      setValue('previousTactic', null);
    }

    if (!cepId) {
      warningToast(t('errors_standard'));
      return;
    }

    const newPreviousTactic = tacticGroups.length > 0 ? tacticGroups[tacticGroups.length - 1] : null;

    if (cepState.cep?.status === CepStatus.APPROVED) {
      await updateTacticRelationsOnApprovedCEP(relatedTacticsId, tacticGroupData, newPreviousTactic, cepId);
    } else {
      await updateTacticRelationsOnNotApprovedCEP(relatedTacticsId, tacticGroupData, newPreviousTactic, cepId);
    }

    setTacticGroupLoading(false);
  };

  const handleTacticGroupChanging = (event: SyntheticEvent, value: { id: string; label: string }) => {
    const selectedGroup = cepStateValue.tacticGroups.find((tactic) => tactic._id === value?.id);
    setSelectedTacticGroup(selectedGroup || null);
  };

  const handleCallToActionChanging = (event: SyntheticEvent, value: { id: string; label: string }) => {
    if (!flags.mayaAkma297EnableSuffixForTacticName) return;

    const formValues = getValues();
    Object.keys(formValues).forEach((element: string) => {
      if (element === 'titleSuffix') {
        const suffixKey = Object.keys(TacticSuffix).find((suffixValue) => suffixValue === value?.id) as keyof typeof TacticSuffix;
        setValue(element, TacticSuffix[suffixKey] || '');
      }
    });
  };
  const handleOpeningGroupForm: React.MouseEventHandler<HTMLButtonElement> = async () => {
    const nonChildTactics = cepStateValue.tactics.filter((tactic) => !tactic.parentTactic);

    if (nonChildTactics.length < minimumAllowedNumberOfTacticsInGroup) {
      warningToast(t('maya_group_form_tactic_number_validation'));
    }

    setOpenGroupForm(true);
  };

  const { isTacticGroupingImprovementsEnabled } = useTacticKanban();
  const showLegacyOrderingFields = !isTacticGroupingImprovementsEnabled;

  const { tacticFormConfig } = useTacticFormConfig(
    tacticStateValue,
    cepStateValue,
    selectedChannel,
    keyMessagesOptions,
    dropMaterialsOptions,
    allApprovedDocumentIdOptions,
    allVeevaVaultDocumentsLoading,
    tacticGroupsOptions,
    selectedTacticGroup,
    tacticTitlesInSelectedGroup,
    handleTacticGroupChanging,
    handleCallToActionChanging,
    previousTacticOptions,
    handleOpeningGroupForm,
    setOpenGroupForm,
    openGroupForm,
    handleTacticGroupCreation,
    showTacticGroup,
    tacticGroupDisabled,
    allIVADocumentsOptions,
    allVeevaVaultIVADocumentsLoading,
    showNotCertifiedDocuments,
    handleVAEFilterChanging,
    executingTeam,
    showNotCertifiedIVADocuments,
    handleIVADocumentsFilterChanging,
  );

  const { isValid } = useWatchForm();

  if (tacticState.tactic) {
    const fieldsName = Object.keys(tacticFormConfig);
    setFieldShowError(true, fieldsName);
  }

  const cancelTacticForm = () => {
    resetForm();
    tacticState.tactic = null;
    tacticState.mode = TacticMode.list;
  };

  const filterChannelSpecificFields = (values: YodaFieldValue, currentChannel: Channel | null) => {
    if (!currentChannel) return values;

    const cleanedValues = { ...values };
    const currentTactic = tacticStateValue.tactic;

    const channelSpecificFields = [
      'titleSuffix',
      'assetName',
      'keyMessage',
      'approvedDocumentId',
      'IVADocumentId',
      'dropMaterial',
      'eventId',
      'destinationUrl',
      'description',
    ];

    channelSpecificFields.forEach((fieldName) => {
      const isPresentInForm = Object.hasOwn(cleanedValues, fieldName);
      const dbValue = currentTactic ? (currentTactic as any)[fieldName] : null;
      const isPresentInDb = dbValue != null && dbValue !== '';

      if (isPresentInDb && !isPresentInForm) {
        cleanedValues[fieldName] = '';
      }
    });

    return cleanedValues;
  };

  const handleSaveTacticForm = async () => {
    setIsSubmitting(true);
    const formValues = getValues();
    const formattedTactic = tacticToBE(formValues, tacticStateValue.cepId, keyMessagesOptions, dropMaterialsOptions, defaultTacticVersion);

    if (flags.mayaBdcfcsd1251ExtendTacticEditingOfApprovedCep) {
      formattedTactic.version = defaultTacticVersion;
    }

    if (cepStateValue.cep?.status === CepStatus.DRAFT || cepStateValue.cep?.status === CepStatus.FEEDBACK) {
      formattedTactic.status = flags.mayaBdcfcsd1251EnableTacticDraftStatus ? TacticStatus.DRAFT : TacticStatus.EMPTY;
    }

    if (cepStateValue.cep?.status === CepStatus.APPROVED) {
      formattedTactic.status = TacticStatus.APPROVED;
    }

    if (isTacticGroupingImprovementsEnabled) {
      formattedTactic.mandatory = false;

      if (!formattedTactic.actionOrder) {
        const maxActionOrder = cepStateValue.tactics.reduce((max, tactic) => (
          (tactic.actionOrder || 0) > max ? (tactic.actionOrder || 0) : max
        ), 0);
        formattedTactic.actionOrder = maxActionOrder + 1;
      }

      formattedTactic.parentTactic = null;
      formattedTactic.previousTactic = null;
    }

    const { data: tacticData, error } = await createTactic(formattedTactic, tacticStateValue.cepId, sendToVeeva);

    const { data: { getCustomerEngagementPlanningById: customerEngagementPlanning } } = await queryGetCepById(
      { variables: { id: cepStateValue.cep?._id } },
    );
    if (cepState.cep) {
      cepState.cep.globalCepSyncStatus = (
        customerEngagementPlanning as CustomerEngagementPlanning
      ).globalCepSyncStatus;
    }

    setIsSubmitting(false);
    if (tacticData && !error) {
      succesToast(t('maya_tactic_created', { title: tacticData.title }));
      cepState.tactics = [...cepState.tactics, tacticData];
      resetForm();
    } else {
      warningToast(error || t('errors_standard'));
    }

    tacticState.tactic = null;
    tacticState.mode = TacticMode.list;
  };

  const isMinorTacticChanges = (inputKey: string, inputValue: any, formConfig: TacticFormConfigType) => {
    const currentTactic: Tactic | null = tacticStateValue.tactic;

    const alwaysTrueKeys = new Set([
      formConfig.title.name,
      formConfig.assetName.name,
      formConfig.parentTactic.name,
      formConfig.previousTactic.name,
      formConfig.destinationUrl.name,
      formConfig.callToAction.name,
      formConfig.isKeyPerformanceIndicator.name,
    ]);

    const conditionalChecks: Record<string, boolean> = {
      [formConfig.keyMessage.name]: !currentTactic?.keyMessage?.id,
      [formConfig.dropMaterial.name]: !currentTactic?.dropMaterial?.id,
      [formConfig.eventId.name]: !currentTactic?.eventId,
    };

    return alwaysTrueKeys.has(inputKey) || (conditionalChecks[inputKey] ?? false);
  };

  const checkMajorTacticChanges = (formConfig: TacticFormConfigType) => {
    const formInputs = getValues();
    const formKeys: string[] = Object.keys(formInputs);

    const listOfChangesFlags = formKeys.map((key) => {
      const formConfigInput = formConfig[key as keyof TacticFormConfigType];
      let inputDefaultValue;

      if (formConfigInput && 'defaultValue' in formConfigInput) {
        inputDefaultValue = formConfigInput.defaultValue === undefined ? '' : formConfigInput.defaultValue;
      } else if (formConfigInput && 'checked' in formConfigInput) {
        inputDefaultValue = formConfigInput.checked;
      }

      if (key === formConfig.date.name) {
        const startDateFromInput = formInputs[key][0].toString();
        const defaultStartDate = inputDefaultValue[0].toString();

        if (startDateFromInput !== defaultStartDate) {
          return true;
        }
      } else if (formInputs[key] !== inputDefaultValue) {
        return !isMinorTacticChanges(key, formInputs[key], formConfig);
      }
    });

    return listOfChangesFlags.includes(true);
  };

  const updateTacticWithNotApprovedStatus = async (tacticId: string, cepId: string, formValues: YodaFieldValue, version: number) => {
    const formattedTactic = tacticToBE(formValues, cepStateValue.cep?._id, keyMessagesOptions, dropMaterialsOptions, version);

    if (cepStateValue.cep?.status === CepStatus.DRAFT || cepStateValue.cep?.status === CepStatus.FEEDBACK) {
      formattedTactic.status = flags.mayaBdcfcsd1251EnableTacticDraftStatus ? TacticStatus.DRAFT : TacticStatus.EMPTY;
    }

    const { data: tacticData, error } = await updateTactic(tacticId, formattedTactic, cepId, sendToVeeva);
    const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepStateValue.cep?._id } });

    if (cepState.cep) {
      cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
    }

    setIsSubmitting(false);
    if (tacticData && !error) {
      succesToast(t('maya_tactic_updated', { title: tacticData.title }));
    } else {
      warningToast(t('errors_standard'));
    }

    tacticState.tactic = null;
    tacticState.mode = TacticMode.list;
    cepState.tactics = [];
  };

  const updateTacticWithApprovedStatus = async (
    tacticId: string,
    cepId: string, formValues: YodaFieldValue,
    isMajorChanges: boolean,
    version: number,
  ) => {
    const formattedTactic = tacticToBE(formValues, cepStateValue.cep?._id, keyMessagesOptions, dropMaterialsOptions, version);
    const { data: tacticData, error } = await updateApprovedTactic(tacticId, formattedTactic, cepId, isMajorChanges, sendToVeeva);
    const { data: customerEngagementPlanning } = await queryGetCepById({ variables: { id: cepStateValue.cep?._id } });

    if (cepState.cep) {
      cepState.cep.globalCepSyncStatus = (customerEngagementPlanning as CustomerEngagementPlanning).globalCepSyncStatus;
    }

    setIsSubmitting(false);
    if (tacticData && !error) {
      succesToast(t('maya_tactic_updated', { title: tacticData.title }));
    } else {
      warningToast(t('errors_standard'));
    }

    tacticState.tactic = null;
    tacticState.mode = TacticMode.list;
    cepState.tactics = [];
  };

  const validatePreviousTacticField = () => {
    const fields = getValues();
    const currentTacticGroupId = fields.tacticGroup;
    const currentPreviousTacticId = fields.previousTactic;
    const previousTacticOptionsLength = previousTacticOptions?.length || 0;

    if (!flags.mayaBdcfcsd1585EnableTacticGroup || !currentTacticGroupId) {
      return true;
    }

    if (previousTacticOptionsLength && currentTacticGroupId && !currentPreviousTacticId) {
      warningToast(t('maya_tactic_form_prev_tactic_validation_required'));
      return false;
    }

    if (currentTacticGroupId && currentTacticGroupId === currentPreviousTacticId) {
      warningToast(t('maya_tactic_form_prev_tactic_validation_not_equal'));
      return false;
    }

    return true;
  };

  const handleUpdateTacticForm = async () => {
    if (!tacticStateValue.tactic) return;

    setIsSubmitting(true);
    const formValues = getValues();
    const tacticId = tacticStateValue.tactic._id;
    const { cepId } = tacticStateValue;
    const isApprovedCEP = cepStateValue.cep?.status === CepStatus.APPROVED;
    const isMajorTacticChanges = isApprovedCEP && checkMajorTacticChanges(tacticFormConfig);
    const currentTacticVersion = tacticStateValue.tactic.version || defaultTacticVersion;
    const tacticVersion = isMajorTacticChanges ? currentTacticVersion : (currentTacticVersion + 1);

    const cleanedFormValues = filterChannelSpecificFields(formValues, channelKey as Channel);

    const mergedFormValues = isTacticGroupingImprovementsEnabled
      ? {
        ...cleanedFormValues,
        actionOrder: tacticStateValue.tactic.actionOrder,
        mandatory: tacticStateValue.tactic.mandatory,
        previousTactic: tacticStateValue.tactic.previousTactic?._id || null,
        parentTactic: tacticStateValue.tactic.parentTactic?._id || null,
      }
      : cleanedFormValues;

    if (!isApprovedCEP || !flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
      || (flags.mayaBdcfcsd653ExtendEditingOfApprovedCep && !flags.mayaBdcfcsd1251ExtendTacticEditingOfApprovedCep)) {
      await updateTacticWithNotApprovedStatus(tacticId, cepId, mergedFormValues, currentTacticVersion);
    } else {
      const numberOfTacticsInGroup = getNumberOfTacticsInGroup(tacticStateValue.tactic._id);
      const updatedGroupId = isTacticGroupingImprovementsEnabled ? tacticStateValue?.tactic?.parentTactic?._id : formValues.tacticGroup;
      const currentGroupId = tacticStateValue?.tactic?.parentTactic?._id;
      const notCorrectNumberOfTactics = numberOfTacticsInGroup && numberOfTacticsInGroup <= minimumAllowedNumberOfTacticsInGroup;

      if (notCorrectNumberOfTactics && currentGroupId && updatedGroupId !== currentGroupId) {
        warningToast(t('tactic_form_submit_number_child_tactic_validation'));
        setIsSubmitting(false);
      } else {
        await updateTacticWithApprovedStatus(tacticId, cepId, mergedFormValues, isMajorTacticChanges, tacticVersion);
      }
    }
  };

  const buildAssetRequestUrl = (channel: Channel): void => {
    if (channel === Channel.group) return;
    if (!RequestFormUrlMapping[channel]) return;

    const assetRequestUrl = RequestFormUrlMapping[channel];
    const searchParams = new URLSearchParams();
    const formValues = getValues();

    const country = cepState.cep?.customerInitiative?.country
      ? InitiativeCountry[cepState.cep?.customerInitiative?.country as keyof typeof InitiativeCountry]
      : '';
    const therapeuticArea = cepState.cep?.therapeuticArea
      ? TherapeuticAreaRequestFormMapping[cepState.cep?.therapeuticArea as keyof typeof TherapeuticAreaRequestFormMapping]
      : '';

    searchParams.append('assetName', formValues[tacticFormConfig.assetName.name] || '');
    searchParams.append('description', formValues[tacticFormConfig.description.name] || '');
    searchParams.append('country', country);
    searchParams.append('therapeuticArea', therapeuticArea);
    searchParams.append('cepId', cepState.cep?.formattedCepId || '');
    searchParams.append('cepName', cepState.cep?.title || '');
    searchParams.append('tacticId', tacticStateValue.tactic?.formattedTacticId || '');

    window.open(`${assetRequestUrl}${searchParams.toString()}`, '_blank');
  };

  const requestAssetButtonConfig = !channelKey || !RequestFormUrlMapping[channelKey as keyof typeof RequestFormUrlMapping] ? null : {
    buttonProps: {
      onClick: () => buildAssetRequestUrl(channelKey as Channel),
      buttonType: ButtonType.primary,
      disabled: !isValid,
    },
    label: t('maya_tactic_form_request_asset_label'),
  };

  useEffect(() => {
    if (channelKey) {
      const formValues = getValues();

      Object.keys(formValues).forEach((element: string) => {
        if (element !== tacticFormConfig.channel.name) {
          setDefaultValue(element, undefined);
          resetField(element);
          setError(element, '');
        }
      });

      setSelectedChannel(channelKey);
    }
  }, [
    cepStateValue.cep?._id,
    channelKey,
    getValues,
    resetField,
    setDefaultValue,
    setError,
    tacticFormConfig.channel.name,
    tacticStateValue.tactic,
  ]);

  const handleSubmitForm = () => {
    const validationStatus = validatePreviousTacticField();

    if (!validationStatus) {
      return;
    }

    const submitForm: Function = tacticStateValue.tactic?._id ? handleUpdateTacticForm : handleSaveTacticForm;
    return submitForm();
  };

  const cancelButtonConfig = {
    buttonProps: {
      onClick: cancelTacticForm,
      buttonType: ButtonType.secondary,
    },
    label: t('actions_cancel_label'),
  };

  /* fix */
  const isSubmitButtonDisabledByChannel = () => {
    const currentChannel = channelKey as Channel | null;
    const baseDisabled = !isValid || isSubmitting || tacticGroupLoading;

    if (!currentChannel) {
      return true;
    }

    switch (currentChannel) {
      case Channel.approvedEmail:
        return baseDisabled || allVeevaVaultDocumentsLoading;

      case Channel.f2fEvent:
      case Channel.hybridEvent:
      case Channel.virtualEvent:
      case Channel.oneToOneIVA:
        return baseDisabled || allVeevaVaultIVADocumentsLoading;

      case Channel.f2fMeeting:
      case Channel.oneToOne:
      case Channel.virtualMeeting:
      case Channel.oneToOneKeyMessage:
      case Channel.telephone:
        return baseDisabled || keyMessagesLoading || dropMaterialsLoading || allVeevaVaultIVADocumentsLoading;

      case Channel.oneToOneDropMaterial:
        return baseDisabled || dropMaterialsLoading;

      default:
        return baseDisabled;
    }
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: handleSubmitForm,
      buttonType: ButtonType.primary,
      disabled: isSubmitButtonDisabledByChannel(),
    },
    label: t('maya_tactic_form_submit_label'),
    loading: isSubmitting,
  };

  const [nextTactics, setNextTactics] = useState<Tactic[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);
  const [rowCountState, setRowCountState] = useState(0);

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const { data, loading, count } = useGetNextTacticsQuery(tacticStateValue.tactic?._id || null, queryOptions);

  useEffect(() => {
    setRowCountState((prevRowCountState) => (count !== undefined ? count : prevRowCountState));
  }, [count, setRowCountState]);

  useEffect(() => {
    if (!loading) {
      setNextTactics(data);
    }
  }, [data, loading]);

  useEffect(() => {
    if (selectedTacticGroup) {
      const filteredTactics = cepStateValue.tactics.filter((tactic) => {
        return tactic._id !== tacticStateValue.tactic?._id && tactic.parentTactic?._id === selectedTacticGroup._id;
      });

      const relatedTacticTitles = filteredTactics.map((tacticItem) => tacticItem.title);

      if (tacticStateValue.tactic?.title) {
        relatedTacticTitles.push(tacticStateValue.tactic?.title);
      }

      setTacticTitlesInSelectedGroup(relatedTacticTitles);
    } else {
      setTacticTitlesInSelectedGroup([]);
    }
  }, [selectedTacticGroup, cepStateValue.tactics, cepStateValue.tacticGroups, tacticStateValue.tactic]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  return {
    cancelButtonConfig,
    keyMessagesLoading,
    dropMaterialsLoading,
    keyMessagesOptions,
    loading,
    nextTactics,
    onPageChange,
    onPageSizeChange,
    providerFields,
    rowCountState,
    selectedChannel,
    submitButtonConfig,
    t,
    tacticFormConfig,
    allVeevaVaultDocumentsLoading,
    allVeevaVaultDocumentsOptions: allApprovedDocumentIdOptions,
    requestAssetButtonConfig,
    tacticGroupLoading,
    showTacticGroup,
    openTacticValidationDialog,
    setOpenTacticValidationDialog,
    allVeevaVaultIVADocumentsLoading,
    showLegacyOrderingFields,
  };
};

export default useTacticCreate;
